'use client'

import React, { Component, ErrorInfo, ReactNode } from 'react'
import CircleLoading from '../circle-loading'
import reportError, {
	installCrashActionRecorder,
	installFreezeReporter,
	installTranslationGuard,
	recoverFromChunkLoadError
} from '@/utils/report-error'

interface Props {
	children: ReactNode
	fallback?: ReactNode
}

interface State {
	hasError: boolean
}

class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props)
		this.state = { hasError: false }
	}

	componentDidMount() {
		// So a crash report can say what the user last clicked.
		installCrashActionRecorder()
		installTranslationGuard()
		// A page that stops answering for seconds throws nothing; this reports it.
		installFreezeReporter()
	}

	static getDerivedStateFromError(_: Error): State {
		return { hasError: true }
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('Error caught by ErrorBoundary:', error, errorInfo)
		void reportError(error, errorInfo?.componentStack ?? undefined)
		// A stale tab after a deploy: fetch the current build instead of
		// showing 'something went wrong' for a chunk that no longer exists.
		recoverFromChunkLoadError(error)
	}

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback
			}

			return (
				<div className="flex flex-col items-center justify-center p-4 h-full">
					<p className="text-sm text-muted-foreground mb-2">
						Something went wrong on our side. Reload the page; if it repeats, email support.
					</p>
					<CircleLoading />
				</div>
			)
		}

		return this.props.children
	}
}

export default ErrorBoundary
